import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu'
import { authService, type User } from '@/services/auth-service'
import { cn } from '@/lib/utils'
import { LogOut, CircleUser } from 'lucide-react'
import { useUserApps } from '@/hooks/useUserApps'
import { useWindows } from '@/hooks/useWindows'
import { useTranslation } from 'react-i18next'
import { useAdaptiveTransition } from '@/hooks/useAdaptiveTransition'

type StartMenuProps = {
  tileClassName?: string
}

export default function StartMenu({ tileClassName }: StartMenuProps) {
  const [user, setUser] = useState<User | null>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { userApps, isLoading } = useUserApps()
  const { getTiming } = useAdaptiveTransition()
  const startMenuMotion = useMemo(() => {
    const enter = getTiming('quick', { context: 'navigation', phase: 'enter', clamp: { min: 140, max: 260 } })
    const exit = getTiming('quick', { context: 'navigation', phase: 'exit', clamp: { min: 140, max: 260 } })
    return {
      '--start-menu-enter-duration': `${enter.duration}ms`,
      '--start-menu-enter-ease': enter.easing,
      '--start-menu-exit-duration': `${exit.duration}ms`,
      '--start-menu-exit-ease': exit.easing,
    } as React.CSSProperties
  }, [getTiming])

  useEffect(() => {
    let mounted = true
    authService
      .getCurrentUser()
      .then((u) => {
        if (mounted) setUser(u)
      })
      .catch(() => {
        // Ignorieren: ProtectedRoute handhabt Redirects
      })
    return () => {
      mounted = false
    }
  }, [])

  const onLogout = async () => {
    await authService.logout()
    navigate('/')
  }
  const { openWindow } = useWindows()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'h-10 w-12 select-none grid place-items-center cursor-pointer',
            'transition-colors hover:bg-black/5 active:bg-black/10 dark:hover:bg-white/5 dark:active:bg-white/10',
            'data-[state=open]:bg-black/10 dark:data-[state=open]:bg-white/10 data-[state=open]:cursor-pointer',
            'focus-visible:outline-none outline-none ring-0',
            tileClassName,
          )}
          aria-label={t('start.openAria')}
          data-driver-id="start-menu-button"
        >
          <span className="sr-only">{t('start.label')}</span>
          <span className="grid grid-cols-3 gap-[2px]">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-[var(--text-color)]" />
            ))}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        className={cn('start-menu__panel', 'w-[300px] h-[400px] rounded-t-lg overflow-hidden shadow-[0_-10px_20px_rgba(0,0,0,0.19)] border-r flex flex-col p-0 bg-[var(--window-background)] border-[var(--window-border-color)]')}
        style={startMenuMotion}
      >
        {/* Header */}
        <div className="w-full border-b border-[var(--window-border-color)] bg-[var(--window-titlebar-bg)]">
          <div className="px-[15px] py-[15px] flex items-center text-[var(--text-color)]">
            <div className="h-9 w-9 rounded-full bg-primary grid place-items-center">
              <CircleUser className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="ml-2.5 text-base max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap">
              {user ? (user.name || user.email) : t('auth.guest')}
            </span>
          </div>
        </div>

        {/* Scrollbarer Mittelteil */}
        <div className="flex-1 overflow-y-auto start-menu-scrollbar bg-[var(--window-background)]">
          <div className="p-2.5">
            {isLoading ? (
              <div className="flex items-center justify-center p-4 text-[var(--text-color)]">
                <span>{t('start.loadingApps')}</span>
              </div>
            ) : (
              userApps.map(({ id, Icon }, index) => (
                <div
                  key={id}
                  className="start-menu__item flex items-center p-2.5 cursor-pointer rounded transition-colors duration-200 ease-in-out text-[var(--text-color)] hover:bg-[var(--sidebar-hover)]"
                  style={{ animationDelay: `${index * 30}ms` }}
                  onClick={() => openWindow(id, t(`apps.${id}`))}
                >
                  <Icon className="h-5 w-5" />
                  <span className="ml-2.5">{t(`apps.${id}`)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto h-[50px] border-t border-[var(--window-border-color)] flex items-center px-[15px] bg-[var(--window-titlebar-bg)] w-full">
          <div
            className="flex items-center cursor-pointer rounded px-3 py-2 transition-colors duration-200 ease-in-out text-[var(--text-color)] hover:bg-[var(--sidebar-hover)]"
            onClick={onLogout}
          >
            <LogOut className="h-5 w-5" />
            <span className="ml-2.5 text-sm">{t('common.logout')}</span>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
